/**
 * Redirect Transaction Utilities
 * 
 * Manages redirect flow transaction state using sessionStorage.
 * Provides transaction tracking, attempt counting, and flow type validation.
 * 
 * MUST FIX #3: Per-transaction attempt counter (not global)
 * MUST FIX #7: FlowType allowlist pattern (extensible, prevents technical debt)
 */

/**
 * MUST FIX #7: Use allowlist pattern for extensibility
 * Only 'signin' is currently supported. To add new flow types:
 * 1. Add to ALLOWED_FLOW_TYPES array
 * 2. Update FlowType type (automatically derived)
 * 3. Add validation logic where needed
 */
const ALLOWED_FLOW_TYPES = ['signin'] as const;
export type FlowType = typeof ALLOWED_FLOW_TYPES[number];

/**
 * Validator function for flow type
 * MUST FIX #7: Use allowlist pattern instead of hard-coded check
 */
export function isValidFlowType(flowType: string): flowType is FlowType {
  return ALLOWED_FLOW_TYPES.includes(flowType as FlowType);
}

export interface RedirectTransaction {
  id: string;
  startedAt: number;
  returnTo?: string;
  flowType: FlowType; // Must be in ALLOWED_FLOW_TYPES
  idp?: string;
}

const MSAL_TXN_KEY = 'msal_txn';
const TXN_MAX_AGE_MS = 15 * 60 * 1000; // 15 minutes
const MAX_REDIRECT_ATTEMPTS = 3; // MUST FIX #3: Per-transaction limit
const REDIRECT_ATTEMPTS_KEY = 'msal_redirect_attempts';

/**
 * Sanitize returnTo path to ensure it's a safe internal path
 * Must be internal path (starts with `/` and not `//`), else default `/dashboard`
 */
export function sanitizeReturnTo(path?: string): string {
  if (!path) {
    return '/dashboard';
  }

  // Must start with / and not be // (external URL)
  if (path.startsWith('/') && !path.startsWith('//')) {
    return path;
  }

  // Default to dashboard for invalid paths
  return '/dashboard';
}

/**
 * Get redirect transaction from sessionStorage
 * Returns null if missing, invalid JSON, or stale (older than TXN_MAX_AGE_MS)
 */
export function getRedirectTransaction(): RedirectTransaction | null {
  try {
    const stored = sessionStorage.getItem(MSAL_TXN_KEY);
    if (!stored) {
      return null;
    }

    const txn: RedirectTransaction = JSON.parse(stored);
    
    // Check if transaction is stale
    const age = Date.now() - txn.startedAt;
    if (age > TXN_MAX_AGE_MS) {
      console.warn('[RedirectTransaction] Transaction expired, clearing:', txn.id);
      clearRedirectTransaction();
      return null;
    }

    // Validate flow type (MUST FIX #7: Use allowlist validator)
    if (!isValidFlowType(txn.flowType)) {
      console.warn('[RedirectTransaction] Invalid flow type, clearing:', txn.flowType);
      clearRedirectTransaction();
      return null;
    }

    return txn;
  } catch (e) {
    console.warn('[RedirectTransaction] Failed to parse transaction:', e);
    clearRedirectTransaction();
    return null;
  }
}

/**
 * Create redirect transaction if none exists (best-effort idempotent)
 * 
 * NOT atomic across tabs/processes - this is intentional.
 * If a fresh txn exists → return null (do not overwrite).
 * Otherwise, create and store new txn.
 * 
 * Item 1: Renamed from 'atomic' to 'best-effort idempotent' with mandatory comment.
 */
export function createRedirectTransactionIfNone(
  returnTo?: string,
  flowType: FlowType = 'signin',
  idp?: string
): RedirectTransaction | null {
  // Validate flow type (MUST FIX #7: Use allowlist validator)
  if (!isValidFlowType(flowType)) {
    console.error('[RedirectTransaction] Invalid flow type:', flowType);
    return null;
  }

  // Check if fresh transaction already exists
  const existing = getRedirectTransaction();
  if (existing) {
    // Transaction exists and is fresh - do not overwrite
    console.log('[RedirectTransaction] Transaction already exists, not creating new one:', existing.id);
    return null;
  }

  // Create new transaction
  const txn: RedirectTransaction = {
    id: `txn-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    startedAt: Date.now(),
    returnTo: sanitizeReturnTo(returnTo),
    flowType,
    idp,
  };

  try {
    sessionStorage.setItem(MSAL_TXN_KEY, JSON.stringify(txn));
    console.log('[RedirectTransaction] Created transaction:', txn.id);
    return txn;
  } catch (e) {
    console.error('[RedirectTransaction] Failed to create transaction:', e);
    return null;
  }
}

/**
 * Clear redirect transaction from sessionStorage
 */
export function clearRedirectTransaction(): void {
  try {
    sessionStorage.removeItem(MSAL_TXN_KEY);
  } catch (e) {
    console.warn('[RedirectTransaction] Failed to clear transaction:', e);
  }
}

/**
 * Get redirect attempts for a specific transaction
 * MUST FIX #3: Per-transaction tracking (not global)
 */
function getRedirectAttempts(): Record<string, number> {
  try {
    const stored = sessionStorage.getItem(REDIRECT_ATTEMPTS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('[RedirectTransaction] Failed to parse attempt counter:', e);
  }
  return {};
}

/**
 * Save redirect attempts to sessionStorage
 * MUST FIX #3: Per-transaction tracking (not global)
 */
function saveRedirectAttempts(attempts: Record<string, number>): void {
  try {
    sessionStorage.setItem(REDIRECT_ATTEMPTS_KEY, JSON.stringify(attempts));
  } catch (e) {
    console.warn('[RedirectTransaction] Failed to save attempt counter:', e);
  }
}

/**
 * Increment redirect attempt counter for a specific transaction
 * MUST FIX #3: Per-transaction limit (prevents multi-tab false lockouts)
 * 
 * @param txnId - Transaction ID
 * @returns New attempt count for this transaction
 */
export function incrementRedirectAttempts(txnId: string): number {
  const attempts = getRedirectAttempts();
  const current = attempts[txnId] || 0;
  const newCount = current + 1;
  attempts[txnId] = newCount;
  saveRedirectAttempts(attempts);
  console.log(`[RedirectTransaction] Incremented attempts for ${txnId}: ${newCount}`);
  return newCount;
}

/**
 * Check if redirect attempt limit has been reached for a transaction
 * MUST FIX #3: Per-transaction limit (prevents multi-tab false lockouts)
 * 
 * @param txnId - Transaction ID
 * @returns true if at or above MAX_REDIRECT_ATTEMPTS for this transaction
 */
export function checkRedirectAttemptLimit(txnId: string): boolean {
  const attempts = getRedirectAttempts();
  const count = attempts[txnId] || 0;
  const atLimit = count >= MAX_REDIRECT_ATTEMPTS;
  if (atLimit) {
    console.warn(`[RedirectTransaction] Attempt limit reached for ${txnId}: ${count} >= ${MAX_REDIRECT_ATTEMPTS}`);
  }
  return atLimit;
}

/**
 * Reset redirect attempt counter for a transaction on successful login
 * MUST FIX #3: Per-transaction reset (prevents stale attempt counts)
 * 
 * @param txnId - Transaction ID
 */
export function resetRedirectAttempts(txnId: string): void {
  const attempts = getRedirectAttempts();
  delete attempts[txnId];
  saveRedirectAttempts(attempts);
  console.log(`[RedirectTransaction] Reset attempts for ${txnId}`);
}

/**
 * StrictMode / Remount Idempotency Guard
 * 
 * Prevents double execution of redirect handler under React StrictMode
 * or component remount scenarios.
 */

const REDIRECT_HANDLED_KEY = 'msal_redirect_handled';
const REDIRECT_HANDLED_TTL_MS = 90 * 1000; // 90 seconds

/**
 * Check if redirect was handled recently (within TTL)
 * Used to prevent StrictMode double execution
 */
export function isRedirectHandledRecently(): boolean {
  try {
    const stored = sessionStorage.getItem(REDIRECT_HANDLED_KEY);
    if (!stored) {
      return false;
    }

    const { timestamp } = JSON.parse(stored);
    const age = Date.now() - timestamp;
    
    if (age > REDIRECT_HANDLED_TTL_MS) {
      // TTL expired, clear and return false
      clearRedirectHandled();
      return false;
    }

    return true;
  } catch (e) {
    console.warn('[RedirectTransaction] Failed to check handled flag:', e);
    return false;
  }
}

/**
 * Mark redirect as handled immediately
 * Set handled immediately after receiving redirect response (before other awaits)
 * This prevents StrictMode double execution
 */
export function markRedirectHandled(): void {
  try {
    const data = {
      timestamp: Date.now(),
    };
    sessionStorage.setItem(REDIRECT_HANDLED_KEY, JSON.stringify(data));
    console.log('[RedirectTransaction] Marked redirect as handled');
  } catch (e) {
    console.warn('[RedirectTransaction] Failed to mark redirect as handled:', e);
  }
}

/**
 * Clear redirect handled flag
 */
export function clearRedirectHandled(): void {
  try {
    sessionStorage.removeItem(REDIRECT_HANDLED_KEY);
  } catch (e) {
    console.warn('[RedirectTransaction] Failed to clear handled flag:', e);
  }
}

/**
 * Check if URL has redirect parameters (indicating we're in a redirect callback)
 * @returns true if URL contains MSAL redirect parameters
 */
function hasRedirectParameters(): boolean {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    // Check for common MSAL redirect parameters
    return (
      urlParams.has('code') ||
      urlParams.has('id_token') ||
      urlParams.has('access_token') ||
      hashParams.has('id_token') ||
      hashParams.has('access_token') ||
      urlParams.has('error') ||
      hashParams.has('error')
    );
  } catch (e) {
    console.warn('[RedirectTransaction] Failed to check redirect parameters:', e);
    return false;
  }
}

/**
 * Clear stale redirect transaction if user navigated back from redirect flow
 * 
 * This function detects when:
 * - A redirect transaction exists in sessionStorage
 * - The current URL has no redirect parameters (user navigated back)
 * 
 * In this case, the transaction is stale and should be cleared to allow
 * the user to initiate a new sign-in flow.
 * 
 * @returns true if a stale transaction was cleared, false otherwise
 */
export function clearStaleTransactionIfNeeded(): boolean {
  try {
    // Check if we have a transaction
    const txn = getRedirectTransaction();
    if (!txn) {
      return false;
    }

    // Check if we're in a redirect callback (have redirect parameters)
    if (hasRedirectParameters()) {
      // We're in a redirect callback, don't clear the transaction
      return false;
    }

    // Transaction exists but no redirect parameters = user navigated back
    // Clear the stale transaction
    console.log('[RedirectTransaction] Clearing stale transaction (user navigated back):', txn.id);
    clearRedirectTransaction();
    clearRedirectHandled();
    
    return true;
  } catch (e) {
    console.warn('[RedirectTransaction] Failed to clear stale transaction:', e);
    return false;
  }
}

/**
 * Cleanup all redirect state (Item 10: Rollback State Hygiene)
 * 
 * Clears all redirect-related sessionStorage entries:
 * - msal_txn (redirect transaction)
 * - msal_redirect_handled (idempotency guard)
 * - msal_redirect_attempts (attempt counter)
 * - msal_auth_error (error state)
 * - login_error (error flag)
 * 
 * This should be called on app start when redirect flow is disabled
 * (VITE_MSAL_USE_REDIRECT=false) to ensure clean state for rollback.
 */
export function cleanupRedirectState(): void {
  try {
    // Clear redirect transaction
    clearRedirectTransaction();
    
    // Clear redirect handled flag
    clearRedirectHandled();
    
    // Clear redirect attempts
    try {
      sessionStorage.removeItem(REDIRECT_ATTEMPTS_KEY);
    } catch (e) {
      console.warn('[RedirectTransaction] Failed to clear attempt counter:', e);
    }
    
    // Clear error state
    try {
      sessionStorage.removeItem('msal_auth_error');
      sessionStorage.removeItem('login_error');
    } catch (e) {
      console.warn('[RedirectTransaction] Failed to clear error flags:', e);
    }
    
    console.log('[RedirectTransaction] Cleaned up all redirect state (rollback hygiene)');
  } catch (e) {
    console.warn('[RedirectTransaction] Failed to cleanup redirect state:', e);
  }
}
